import { useState } from "react";
import { Moon, Plus, Sun } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { useAppData } from "../../contexts/AppDataContext";
import { useTheme } from "../../contexts/ThemeContext";
import { SHELL } from "../../constants/testIds";
import { GlobalSearch } from "./GlobalSearch";
import { NewMemberDialog } from "./NewMemberDialog";
import { TrialStatusBadge } from "../trial/TrialStatusBadge";

// UI_STANDARDS.md B4/B6 EL-010: glass top bar — search, branch context switcher, primary
// button, theme toggle, avatar dropdown (also glass). Stat cards/table stay solid (elsewhere).
export function TopBar() {
  const { me, branches, selectedBranchId, setSelectedBranchId, logout } = useAppData();
  const { theme, toggleTheme } = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const initials = (me?.name || me?.email || "U").slice(0, 2).toUpperCase();

  return (
    <header data-testid={SHELL.topBar} className="glass-surface sticky top-0 z-40 flex items-center gap-4 px-6 py-3 mx-4 mt-4 rounded-[14px]">
      <GlobalSearch />

      <TrialStatusBadge trialStatus={me?.trial_status} />

      <select
        data-testid={SHELL.branchSwitcher}
        value={selectedBranchId}
        onChange={(e) => setSelectedBranchId(e.target.value)}
        className="scarlet-focusable bg-transparent text-sm font-medium border rounded-[10px] px-3 py-2 outline-none"
        style={{ borderColor: "var(--scarlet-hairline)", color: "var(--scarlet-text)" }}
      >
        <option value="all">All branches</option>
        {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>

      <Button
        data-testid={SHELL.newMemberButton}
        onClick={() => setDialogOpen(true)}
        className="text-white shrink-0"
        style={{ background: "var(--scarlet-accent)" }}
      >
        <Plus size={16} /> New member
      </Button>
      <NewMemberDialog open={dialogOpen} onOpenChange={setDialogOpen} />

      <button
        data-testid={SHELL.themeToggle}
        onClick={toggleTheme}
        aria-label="Toggle day/night theme"
        className="scarlet-focusable p-2 rounded-[10px] hover:bg-black/5 shrink-0"
      >
        {theme === "dark" ? <Sun size={18} strokeWidth={1.7} /> : <Moon size={18} strokeWidth={1.7} />}
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={SHELL.avatarMenuTrigger} className="scarlet-focusable shrink-0 rounded-full">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="text-white text-xs font-semibold" style={{ background: "var(--scarlet-accent)" }}>{initials}</AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="glass-dropdown border-0 rounded-[14px]">
          <DropdownMenuLabel>
            <p className="text-sm font-semibold">{me?.name}</p>
            <p className="text-xs font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>{me?.email}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid={SHELL.avatarMenuLogout} onClick={logout}>Log out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
